// @flow
import {
  useStoreFeatures,
  currentUserHasFeature,
} from '../../../utils/component';
import {useUserType} from '../../auth/utils';
import type {StoreFeature} from '../../../entities';
import {useMemo} from 'react';

/**
 * Hook to verify multiple features for the current user session
 * @param {string[]} features - List of features to verify
 * @returns {Object} - Object with the state of each feature for current user
 */

type CurrentUserFeatureStates = {[StoreFeature]: boolean};

export function useCurrentUserFeatures(
  features: StoreFeature[]
): CurrentUserFeatureStates {
  const storeFeatures = useStoreFeatures();
  const {isCollaborator} = useUserType();

  const currentUserFeatureStates = useMemo(() => {
    const result = {};
    features.forEach(feature => {
      result[feature] = currentUserHasFeature(
        feature,
        storeFeatures,
        isCollaborator
      );
    });
    return result;
  }, [features, storeFeatures, isCollaborator]);

  return currentUserFeatureStates;
}

/**
 * Hook to verify if current user has ALL the specified features
 * @param {string[]} features - List of features that MUST be available
 * @returns {boolean} - true if current user has ALL features
 */
export function useCurrentUserHasAllFeatures(
  features: StoreFeature[]
): boolean {
  const currentUserFeatureStates = useCurrentUserFeatures(features);
  return features.every(feature => currentUserFeatureStates[feature]);
}

/**
 * Hook to verify if current user has any of the specified features
 * @param {string[]} features - List of features
 * @returns {boolean} - true if current user has AT LEAST ONE feature
 */
export function useCurrentUserHasAnyFeature(features: StoreFeature[]): boolean {
  const currentUserFeatureStates = useCurrentUserFeatures(features);
  return features.some(feature => currentUserFeatureStates[feature]);
}

/**
 * Hook to get features missing for current user
 * @param {string[]} features - List of features to verify
 * @returns {StoreFeature[]} - List of features that current user does NOT have
 */
export function useCurrentUserMissingFeatures(
  features: StoreFeature[]
): StoreFeature[] {
  const currentUserFeatureStates = useCurrentUserFeatures(features);
  return features.filter(feature => !currentUserFeatureStates[feature]);
}
